import {inject, Injectable, Injector} from '@angular/core';
import Hashids from 'hashids';
import {AUTHENTICATION_SERVICE, AuthenticationService} from '../authentication/authentication.service';

type ComparableId = string | number | null | undefined;

@Injectable({
  providedIn: 'root',
})
export class IdNormalizationService {
  private readonly injector = inject(Injector);
  private authenticationService: AuthenticationService | null | undefined;

  equalsRuntimeIdAndEnvId(runtimeId: ComparableId, envId: ComparableId): boolean {
    const normalizedRuntimeId = this.normalize(runtimeId);
    const normalizedEnvId = this.normalize(envId);

    return normalizedRuntimeId !== null &&
      normalizedEnvId !== null &&
      normalizedRuntimeId === normalizedEnvId;
  }

  normalize(id: ComparableId): number | null {
    if (id === null || typeof id === 'undefined') {
      return null;
    }

    if (typeof id === 'number') {
      return Number.isInteger(id) ? id : null;
    }

    const trimmedId = id.trim();

    if (trimmedId.length === 0) {
      return null;
    }

    if (/^-?\d+$/.test(trimmedId)) {
      return Number.parseInt(trimmedId, 10);
    }

    const userId = this.getAuthenticationService()?.userInfo?.userId;

    if (!userId) {
      return null;
    }

    const decodedId = new Hashids(userId, 16).decode(trimmedId);

    if (decodedId.length !== 1 || !Number.isInteger(decodedId[0])) {
      return null;
    }

    return decodedId[0] as number;
  }

  private getAuthenticationService(): AuthenticationService | null {
    if (typeof this.authenticationService === 'undefined') {
      this.authenticationService = this.injector.get<AuthenticationService | null>(AUTHENTICATION_SERVICE, null);
    }

    return this.authenticationService ?? null;
  }
}
